import type { QuillConfig } from 'ngx-quill/config'
import { EMAIL_COMPOSER_ZOHO_CONSTANTS } from '@app/shared/constants/email-composer-zoho.constants'

const SEND_TO_CLIENT_TEMPLATE_EVENT_KEY_S2 = 'deal_contract_sent_to_client_s2'

export const EMAIL_COMPOSER_DIALOG_CONSTANTS = {
    DEFAULT_TEMPLATE_ASSET_PATH: '/assets/mock/shiksha-email-template.json',
    DEFAULT_TEMPLATE_EVENT_KEY: 'default_email_template',
    STAGE_2_SEND_TO_CLIENT_TEMPLATE_EVENT_KEY: SEND_TO_CLIENT_TEMPLATE_EVENT_KEY_S2,
    STAGE_3_SEND_TO_CLIENT_TEMPLATE_EVENT_KEY: SEND_TO_CLIENT_TEMPLATE_EVENT_KEY_S2,
    STAGE_4_SEND_TO_CLIENT_TEMPLATE_EVENT_KEY: 'deal_contract_sent_to_client_s4',
    SEND_TO_CLIENT_DOCUMENT_KIND: {
        PROPOSAL: 'proposal',
        CONTRACT: 'contract',
    } as const,
    DIALOG_WIDTH: 'min(900px, calc(100vw - 2rem))',
    DIALOG_MAX_WIDTH: '960px',
    DIALOG_MAX_HEIGHT: 'min(92vh, 880px)',
    MAX_ATTACHMENTS: 20,
    ATTACHMENT_RULES_SHORT: 'PDF, Word, Excel, Images · max 10 MB each',
    TITLE: 'COMPOSE EMAIL',
    SUBTITLE: '',
    CLOSE_ARIA_LABEL: 'Close compose window',
    LABELS: {
        TO: 'To',
        CC: 'Cc',
        BCC: 'Bcc',
        SUBJECT: 'Subject',
        BODY: 'Message',
        ATTACHMENTS: 'Attachments',
        ADD_RECIPIENT: 'Add',
        RECIPIENT_EMAIL_PLACEHOLDER: 'Email(s), comma-separated',
        ADD_RECIPIENT_HINT: 'Add recipient',
        NO_ATTACHMENTS: 'No files attached',
        DRAG_HINT: 'or drop here',
        ATTACH_ZONE_TITLE: 'Browse or drop files',
        LOADING_COMPOSER: 'Loading composer…',
        LOADING_GENERIC: 'Loading...',
        LOADING_DOCUMENTS: 'Loading documents…',
        NO_DOCUMENTS_FOUND: 'No documents found.',
        ATTACH_EXISTING: 'Attach existing',
        ATTACH: 'Attach',
        UPLOADING: 'Uploading…',
        REMOVING: 'Removing…',
        UPLOAD_FAILED: 'Upload failed',
        WORKING: 'Working…',
        SEARCH_DOCUMENTS_PLACEHOLDER: 'Search documents…',
        CLOSE_EXISTING_DOCS: 'Close existing documents',
        HIDE_CC_FIELD: 'Hide Cc field',
        RECIPIENTS_REGION: 'Recipients',
        RECIPIENT_SHORTCUTS: 'Recipient field shortcuts',
        ATTACHMENTS_REGION: 'Attachments',
        LINK_BADGE: 'Link',
    },
    LINKS: {
        SHOW_BCC: 'Bcc',
        HIDE_BCC: 'Hide',
        HIDE_BCC_FULL: 'Hide Bcc field',
    },
    PLACEHOLDERS: {
        SUBJECT: 'Add a subject',
        BODY: 'Write your message…',
    },
    ACTIONS: {
        SEND: 'Send',
        CANCEL: 'Discard',
        REMOVE_FILE: 'Remove',
        VIEW_FILE: 'View',
        REMOVE_CHIP: 'Remove',
        LOAD_MORE: 'Load more',
        LOADING_MORE: 'Loading…',
    },
    RECIPIENT_KIND: {
        TO: 'To',
        CC: 'Cc',
        BCC: 'Bcc',
    } as const,
    LIMITS: {
        EMAIL_MAX_LENGTH: 254,
        SUBJECT_MIN: 3,
        SUBJECT_MAX: 200,
        BODY_MIN: 10,
        BODY_MAX_CHARS: 8000,
        BODY_MAX_WORDS: 1200,
        BODY_MAX_IMAGES: 3,
        TOOLTIP_EMAIL_CHARS: 28,
        TOOLTIP_FILENAME_CHARS: 32,
    },
    MESSAGES: {
        TEMPLATE_LOAD_FAILED: 'Template couldn’t load. You can still write your email.',
        DEFAULT_ATTACHMENT_FETCH_FAILED:
            'Couldn’t attach the proposal file. You can add it manually.',
        DEFAULT_ATTACHMENT_ADDED_AS_LINK:
            'Proposal file couldn’t be downloaded due to CORS. Added it as a link instead.',
        POC_EMAIL_MISSING: 'POC email not found in contact book. Please add a recipient in To.',
        MAX_FILES: 'Limit is 20 attachments.',
        ATTACHMENTS_REJECT_TYPE: 'Skipped - only PDF, Word, Excel, or images.',
        ATTACHMENTS_REJECT_SIZE: 'Skipped - max 10 MB per file.',
        ATTACHMENTS_SKIPPED: 'Some files were skipped (type or size).',
        FILE_ATTACHED_ONE: '1 file attached.',
        FILES_ATTACHED_N: '{n} files attached.',
        FILE_REMOVED: 'Removed “{name}”.',
        INVALID_EMAIL: 'Enter a valid email address.',
        DUPLICATE_EMAIL: 'That address is already in To, Cc, or Bcc.',
        TO_REQUIRED: 'Add at least one recipient in To.',
        SUBJECT_REQUIRED: 'Subject is required.',
        SUBJECT_TOO_SHORT: 'Subject must be at least 3 characters.',
        SUBJECT_TOO_LONG: 'Subject must be at most 200 characters.',
        BODY_REQUIRED: 'Message can’t be empty.',
        BODY_TOO_SHORT: 'Message must be at least 10 characters.',
        BODY_TOO_LONG: 'Message is too long. Please keep it under 8000 characters.',
        BODY_TOO_MANY_WORDS: 'Message is too long. Please keep it under 1200 words.',
        BODY_TOO_MANY_IMAGES: 'You can add at most 3 images in the message body.',
        EMAIL_TOO_LONG: 'Each email must be at most 254 characters.',
        IMAGE_UPLOAD_IN_PROGRESS: 'Image upload in progress. Please wait.',
        ATTACHMENTS_STILL_UPLOADING: 'Attachments are still uploading. Please wait.',
        STAGE_UPDATED_AND_SENT: 'Stage updated and email sent.',
        STAGE_UPDATED_AND_ZOHO_DRAFT_SAVED: 'Stage updated and draft saved in Zoho Mail.',
        FAILED_TO_SEND_EMAIL: 'Failed to send email.',
        EMAIL_SENT_TOAST: 'Email sent.',
        UPLOAD_FAILED: 'Upload failed',
        IMAGE_UPLOAD_FAILED_INLINE: '[Image upload failed]',
        FILES_ADDED_WITH_SKIPS: '{n} added; some files skipped (type or 10 MB limit).',
    },
    DURATIONS: {
        INLINE_SHORT: 4000,
        INLINE_ATTACHMENTS_WAIT: 5000,
        STAGE_SUCCESS: 3500,
        ZOHO_CONGRATS_TOAST: 5200,
        INLINE_ERROR_LONG: 6000,
        INLINE_ATTACHMENT_SKIP: 6500,
        POC_WARNING: 9000,
        TEMPLATE_ERROR: 10000,
        FILE_REMOVED: 3500,
        DEFAULT_ATTACHMENT_REJECT: 6000,
    },
    ICON_NAMES: {
        CLOSE: 'close',
        CLOUD_UPLOAD: 'cloud-upload',
        VISIBILITY: 'visibility',
        DELETE: 'delete',
        PICTURE_AS_PDF: 'picture_as_pdf',
        IMAGE: 'image',
        DESCRIPTION: 'description',
        TABLE_CHART: 'table_chart',
        INSERT_DRIVE_FILE: 'insert_drive_file',
        ATTACH_FILE: 'attach_file',
        SEARCH: 'search',
        ADD: 'add',
        LINK: 'link',
        ZOHO_MAIL: 'mail',
        SEND: 'send_horizontal',
    } as const,
    MIME_TYPES: {
        APPLICATION_OCTET_STREAM: 'application/octet-stream',
        IMAGE_PNG: 'image/png',
        IMAGE_PREFIX: 'image/',
        APPLICATION_PDF: 'application/pdf',
        MSWORD: 'application/msword',
        WORD_OPEN_XML: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        MS_EXCEL: 'application/vnd.ms-excel',
        EXCEL_OPEN_XML: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    } as const,
    DEFAULTS: {
        RECIPIENT_UNDO_MAX: 40,
        ELLIPSIS_FILENAME_MAX_CHARS: 52,
        EMPTY_URL_PLACEHOLDER: '#',
        POC_NAME_UNAVAILABLE_TAG: '[POC is Unavailable]',
        NOTIFICATION_CHANNEL: 'email',
        NOTIFICATION_LOCALE: 'en',
        FALLBACK_FILE_LABEL: 'file',
        AVATAR_FALLBACK: '?',
        BYTES_PER_KB: 1024,
        RECIPIENTS_REF_MAX_FRAMES: 60,
    },
    FILE_SIZE_UNITS: {
        BYTE: 'B',
        KILOBYTE: 'KB',
        MEGABYTE: 'MB',
        GIGABYTE: 'GB',
    } as const,
    DOC_TYPE_LABELS: {
        OTHER: 'Other',
        PROPOSAL: 'Proposal',
        CONTRACT: 'Contract',
    } as const,
    DOC_TYPES_LOWER: {
        PROPOSAL: 'proposal',
        CONTRACT: 'contract',
        OTHER: 'other',
    } as const,
    EMAIL_ATTACHMENT_DOC_TYPE: 'email_attachment' as const,
    UPLOAD_KEY_SUFFIX: '/email-attachment',
    KEYBOARD: {
        BACKSPACE: 'Backspace',
        ENTER: 'Enter',
        COMMA: ',',
        TAB: 'Tab',
        SPACE: ' ',
    } as const,
    QUILL: {
        SOURCE_USER: 'user',
        SOURCE_SILENT: 'silent',
        EMBED_IMAGE: 'image',
        ALIGN_CLASS_CENTER: 'ql-align-center',
        ALIGN_CLASS_RIGHT: 'ql-align-right',
        ALIGN_CLASS_JUSTIFY: 'ql-align-justify',
    } as const,
    HTML_TAGS: {
        OL: 'ol',
        UL: 'ul',
        BR: 'br',
        P: 'p',
        LI: 'li',
        DIV: 'div',
        QUILL_UI_SELECTOR: 'span.ql-ui',
        LIST_SELECTOR: 'ol, ul',
        DIRECT_CHILD_LI_SELECTOR: ':scope > li',
    } as const,
    ORDERED_LIST_TYPES_BY_DEPTH: ['1', 'a', 'i'] as const,
    FILE_EXTENSIONS: {
        PDF: 'pdf',
        DOC: 'doc',
        DOCX: 'docx',
        XLS: 'xls',
        XLSX: 'xlsx',
    } as const,
    IMAGE_EXTENSIONS: [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'webp',
        'bmp',
        'tif',
        'tiff',
        'heic',
        'heif',
    ] as const,
    VALIDATION_KEYS: {
        REQUIRED: 'required',
        MIN_LENGTH: 'minlength',
        MAX_LENGTH: 'maxlength',
        MAX_WORDS: 'maxwords',
    } as const,
    ATTACHMENT_REJECT_REASON: {
        SIZE: 'size',
        TYPE: 'type',
    } as const,
    APPEND_SKIP_KEYS: {
        BOTH: 'both',
        SIZE_ONLY: 'sizeOnly',
        TYPE_ONLY: 'typeOnly',
    } as const,
    PLACEHOLDER_KEYS: {
        COUNT: '{n}',
        NAME: '{name}',
    } as const,
    URL_KEYS: {
        QUERY: '?',
        PARAM_SEPARATOR: '&',
        CACHE_BUST_PARAM: 'cb',
    } as const,
    ZOHO: EMAIL_COMPOSER_ZOHO_CONSTANTS,
    WINDOW_OPEN_FEATURES: 'noopener,noreferrer',
    SELECTORS: {
        QL_EDITOR: '.ql-editor',
        SUBJECT_INPUT: 'input#email-composer-subject, #email-composer-subject',
        RECIPIENT_INLINE_INPUT: 'input.email-composer-recipients__inline-input',
    },
    UNDO_REDO: {
        KEY_Z: 'z',
        KEY_Y: 'y',
    },
} as const

export const EMAIL_COMPOSER_QUILL_CONFIG: QuillConfig = {
    format: 'html',
    theme: 'snow',
    sanitize: true,
    bounds: 'self',
    trackChanges: 'user',
    placeholder: EMAIL_COMPOSER_DIALOG_CONSTANTS.PLACEHOLDERS.BODY,
    modules: {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ script: 'sub' }, { script: 'super' }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            ['blockquote', 'code-block'],
            ['link'],
            [{ color: [] }, { background: [] }],
            ['clean'],
        ],
    },
}

export const EMAIL_BODY_INLINE_STYLES = {
    WRAPPER_DIV:
        'font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.35;color:#202124;',
    PARAGRAPH_BASE: 'margin:0;line-height:1.35;',
    LIST_BASE: 'margin:0;padding-left:20px;line-height:1.35;',
    LIST_ITEM: 'margin:0;padding:0;line-height:1.35;',
    IMG: 'max-width:100%;height:auto;display:block;margin:8px 0;border:0;outline:none;text-decoration:none;',
    TAB_REPLACEMENT: '&nbsp;&nbsp;&nbsp;&nbsp;',
    BR_DOUBLE: '<br><br>',
    EMPTY_P: '<p><br></p>',
    ALIGN_CENTER: 'text-align:center;',
    ALIGN_RIGHT: 'text-align:right;',
    ALIGN_JUSTIFY: 'text-align:justify;',
    IMG_ALT_EMPTY: '',
} as const

export const EMAIL_COMPOSER_ATTACHMENT_LIMITS = {
    MAX_BYTES: 10 * 1024 * 1024,
    FILE_INPUT_ACCEPT: '.pdf,.doc,.docx,.xls,.xlsx,image/*,application/pdf',
} as const

export const EMAIL_COMPOSER_TEMPLATE = {
    RECIPIENT_SPLIT_DELIMITER: ',' as const,
} as const

export const EMAIL_COMPOSER_PASTE = {
    PASTED_FILE_PREFIX: 'pasted-image-',
    PASTED_FILE_EXT: '.png',
    UPLOADING_TOKEN_TEMPLATE: '[Uploading image: {name} ...]',
    DEFAULT_IMAGE_NAME: 'image',
} as const

export const EMAIL_COMPOSER_UPLOAD_STATES = {
    UPLOADING: 'uploading',
    DELETING: 'deleting',
    FAILED: 'failed',
} as const

export const DIALOG_INLINE_MESSAGE_CONSTANTS = {
    STACK_ANIMATION_DELAY_MS: 40,
} as const
